import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  AdminUser,
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
  loadTokens,
  clearTokens,
  isAuthenticated as checkAuth,
} from '../api/adminClient';

interface AdminAuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (resource: string, action: string) => boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

// TEMPORARY: Mock user for UI preview (remove after fixing login)
const MOCK_USER: AdminUser = {
  id: '49567f1d-0d7e-47f1-8c2c-eb91f37c9d6c',
  org_id: '00000000-0000-0000-0000-000000000000',
  email: 'admin@example.com',
  first_name: 'Admin',
  last_name: 'User',
  status: 'active',
  last_login_at: new Date().toISOString(),
  created_at: new Date().toISOString(),
  roles: [{ id: '1', name: 'super_admin', display_name: 'Super Admin', description: null, hierarchy_level: 0, is_system_role: true }],
  permissions: [
    'admin_users:create', 'admin_users:read', 'admin_users:update', 'admin_users:delete',
    'orgs:create', 'orgs:read', 'orgs:update', 'orgs:delete',
    'ownership:create', 'ownership:read', 'ownership:update', 'ownership:delete', 'ownership:bulk',
    'audit:read', 'audit:export',
    'governance:read', 'governance:write',
    'simulator:use',
    'users:read', 'users:create', 'users:update', 'users:delete',
    'roles:read', 'roles:create', 'roles:update', 'roles:delete',
    'reports:read', 'reports:generate', 'reports:export',
    'settings:read', 'settings:update',
  ],
};

// Set to true to bypass login for UI preview during local development.
// MUST be false in production — when true, the dashboard injects a mock
// admin user and renders the entire admin layout to anyone visiting
// /admin without authenticating. Data is still safe (the backend API
// still demands a valid JWT), but the UI shell + tool layout becomes
// publicly visible, which leaks the existence of every admin feature.
const BYPASS_AUTH = false;

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(BYPASS_AUTH ? MOCK_USER : null);
  const [isLoading, setIsLoading] = useState(!BYPASS_AUTH);

  useEffect(() => {
    if (BYPASS_AUTH) return; // Skip auth check when bypassing
    loadTokens();
    if (checkAuth()) {
      refreshUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  async function refreshUser() {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
      clearTokens();
    }
  }

  async function login(email: string, password: string) {
    const result = await apiLogin(email, password);
    setUser(result.user);
  }

  async function logout() {
    try {
      await apiLogout();
    } finally {
      setUser(null);
    }
  }

  function hasPermission(resource: string, action: string): boolean {
    if (!user?.permissions) return false;
    const permission = `${resource}:${action}`;
    return user.permissions.includes(permission) || user.permissions.includes(`${resource}:*`);
  }

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshUser,
        hasPermission,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}
